"use client";

import React, {PropsWithChildren, ReactNode, useState, useEffect} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {FCWithHTMLAttributes} from "../types/reactTypes";
import clsMerge from "../util/clsMerge";
import {useId} from "react";
import * as _ from "lodash-es";
import {faPlus} from "@fortawesome/free-solid-svg-icons/faPlus";
import {faMinus} from "@fortawesome/free-solid-svg-icons/faMinus";

type AccordionClasses = {
	root?: string;
	header?: string;
	panel?: string;
	open?: {
		root?: string;
		header?: string;
		panel?: string;
	};
};

export interface AccordionProps extends PropsWithChildren {
	headerText: string;
	headerIconOpen?: ReactNode;
	headerIconClosed?: ReactNode;
	headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
	listId?: number;
	classes?: AccordionClasses;
	isOpenControlled?: boolean;
	onClickHeader?: (listId: number | undefined) => void;
}

// Can be controlled by a parent by passing isOpenControlled and onClickHeader
// or uncontrolled if these are not provided.

const Accordion: FCWithHTMLAttributes<AccordionProps> = ({
	headerText,
	headerIconOpen = (
		<FontAwesomeIcon icon={faMinus} className="text-orange-700" />
	),
	headerIconClosed = (
		<FontAwesomeIcon icon={faPlus} className="text-orange-700" />
	),
	headingLevel = 3,
	classes,
	children,
	listId,
	isOpenControlled,
	onClickHeader,
	...props
}) => {
	const [isOpen, setIsOpen] = useState<boolean>(!!isOpenControlled);
	const [panelHeight, setPanelHeight] = useState<number>(0);
	const headerId = useId();
	const panelId = useId();
	const panelRef = React.useRef<HTMLDivElement>(null);
	const HeaderTag: keyof JSX.IntrinsicElements = `h${headingLevel}`;

	let isDisplayOpen = isOpen;
	if (isOpenControlled !== undefined) {
		isDisplayOpen = isOpenControlled;
	}

	// Height transitions won't work unless the new height of the element is known when the transition
	// starts. Therefore things like height 100% or max-height won't work, and the height of the panel
	// has to be tracked as a state.
	const handleResize = () => {
		setPanelHeight(panelRef.current?.scrollHeight || 0);
	};

	useEffect(() => {
		handleResize();
		window.addEventListener("resize", _.throttle(handleResize, 200));
	}, [panelId]);

	// Pass event to parent if controlled, otherwise handle locally if uncontrolled
	const handleClickHeader = () => {
		if (onClickHeader) {
			onClickHeader(listId);
		} else {
			setIsOpen((open) => !open);
		}
	};

	return (
		<div
			className={clsMerge(
				"flex flex-col border-b-2 border-gray-200 last:border-b-0",
				classes?.root,
				isDisplayOpen && classes?.open?.root
			)}
			{...props}
		>
			<HeaderTag>
				<button
					id={headerId}
					className={clsMerge(
						"flex w-full flex-row items-center gap-4 py-3 text-left text-lg sm:gap-10 sm:px-4 sm:py-6 sm:text-h3",
						classes?.header,
						isDisplayOpen && classes?.open?.header
					)}
					onClick={handleClickHeader}
					aria-controls={panelId}
					aria-expanded={isDisplayOpen}
				>
					{isDisplayOpen ? headerIconOpen : headerIconClosed}
					{headerText}
				</button>
			</HeaderTag>
			<div
				className="overflow-hidden transition-height duration-300 ease-out"
				style={{height: isDisplayOpen ? panelHeight : 0}}
			>
				<div
					id={panelId}
					ref={panelRef}
					className={clsMerge(
						"pb-3 pl-8 text-sm sm:pb-6 sm:pl-[4.8125rem] sm:pr-4 sm:text-md",
						classes?.panel,
						isDisplayOpen && classes?.open?.panel
					)}
					role="region"
					aria-labelledby={headerId}
					aria-hidden={!isDisplayOpen}
				>
					{children}
				</div>
			</div>
		</div>
	);
};

export default Accordion;
